/**
 * ConflictBadge.tsx
 * Small corner glyph indicating a booking is part of a conflict.
 *
 * Accessibility (2026-06-20 UX audit): hard and soft conflicts must NOT be
 * distinguished by hue alone. They now carry distinct SHAPES so a colour-blind
 * planner can tell them apart on the bar itself:
 *   - `type='hard'` → a filled top-right corner TRIANGLE, red   (overlap — sharp/urgent)
 *   - `type='soft'` → a small top-right DOT,             orange (missing capability — milder)
 * Hard takes precedence when a booking has both types. Both keep the tooltip +
 * aria-label naming the severity. Legends mirror the same two shapes.
 *
 * Small enough not to obscure bar labels, visible enough to catch attention.
 */

import React from 'react';
import { Tooltip } from '@mui/material';
import { useTheme } from '@mui/material/styles';

export type ConflictType = 'hard' | 'soft';

interface ConflictBadgeProps {
  /** 'hard' = overlapping bookings (red triangle), 'soft' = missing capability (orange dot). */
  type: ConflictType;
  /** Optional tooltip suffix for more context. */
  detail?: string;
}

export const ConflictBadge: React.FC<ConflictBadgeProps> = ({ type, detail }) => {
  const theme = useTheme();
  const isHard = type === 'hard';

  const color = isHard ? theme.schedule.conflictHard : theme.schedule.conflictSoft;

  const label = isHard
    ? `Hard Conflict (overlapping bookings on this machine)${detail ? ` — ${detail}` : ''}`
    : `Soft Conflict (machine missing capability)${detail ? ` — ${detail}` : ''}`;

  // Distinct shape per severity — see ConflictBadgeShape for the matching legend swatch.
  const shapeStyle: React.CSSProperties = isHard
    ? {
        // Filled top-right corner triangle (CSS border trick).
        width: 0,
        height: 0,
        borderStyle: 'solid',
        borderWidth: '10px 10px 0 0',
        borderColor: `${color} transparent transparent transparent`,
      }
    : {
        // Small dot, ringed in white so it stays visible over any bar colour.
        width: 8,
        height: 8,
        borderRadius: '50%',
        backgroundColor: color,
        border: `1px solid ${theme.palette.background.paper}`,
      };

  return (
    <Tooltip title={label} placement="top" enterDelay={200} arrow>
      <div
        aria-label={label}
        data-testid={`conflict-badge-${type}`}
        style={{
          position: 'absolute',
          top: isHard ? 0 : 1,
          right: isHard ? 0 : 1,
          zIndex: 5,
          pointerEvents: 'auto',
          cursor: 'pointer',
          ...shapeStyle,
        }}
      />
    </Tooltip>
  );
};

/**
 * Static, theme-free legend swatch mirroring ConflictBadge's two shapes (filled
 * triangle = hard, dot = soft). Used by the schedule legends so the key matches
 * what the bars draw. `size` controls the swatch box (default 14px).
 */
export const ConflictBadgeShape: React.FC<{ type: ConflictType; color: string; ringColor: string; size?: number }> = ({
  type,
  color,
  ringColor,
  size = 14,
}) => {
  const isHard = type === 'hard';
  return (
    <span
      style={{ position: 'relative', display: 'inline-block', width: size, height: size, flexShrink: 0 }}
      aria-hidden="true"
    >
      {isHard ? (
        <span
          style={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: 0,
            height: 0,
            borderStyle: 'solid',
            borderWidth: `${size}px ${size}px 0 0`,
            borderColor: `${color} transparent transparent transparent`,
          }}
        />
      ) : (
        <span
          style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            width: size * 0.6,
            height: size * 0.6,
            borderRadius: '50%',
            backgroundColor: color,
            border: `1px solid ${ringColor}`,
          }}
        />
      )}
    </span>
  );
};
